import {
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from 'reactstrap';
import React, { useEffect, useState } from 'react';

import { Button } from '../../components';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { AuspiceDto, InstitutionTypeDto, ProfitVsPublicDto } from '../../common/Types';
import { reportService } from '../../services/ReportService';
import MultiSelect from 'react-multi-select-component';
import styled from 'styled-components';
import { institutionService } from '../../services/InstitutionService';
import { PagingDto } from '../../types';

const StyledMultiSelect = styled(MultiSelect)`
    width: 300px;

    & .dropdown-container {
        width: 100%;
        max-width: 100%;
    }
    & .dropdown-content {
        z-index: 3;
        border: none !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        
        /* Remove borders from ALL nested elements */
        *, & > *, & * {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
        }
        
        /* Force all child elements to have minimal left padding */
        & > * {
            padding-left: 0.25rem !important;
        }
        
        /* Position checkboxes properly to the left */
        input, [role="checkbox"], .checkbox, span, div {
            margin-left: -20px !important;
            margin-right: 0.5rem !important;
        }
    }
    & .select-item {
        display: flex !important;
        align-items: center !important;
        padding-left: 0.125rem !important;
        padding-right: 0.5rem !important;
        
        /* Ensure selection background spans full width */
        &.selected {
            background-color: rgba(13, 110, 253, 0.1) !important;
            width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0.25rem !important;
        }
        
        /* Override the negative margins for selected items to fill width */
        &.selected input, &.selected [role="checkbox"], &.selected .checkbox, 
        &.selected span, &.selected div {
            margin-left: 0 !important;
        }
    }
`;

type Option = {
    label: string;
    value: string;
};

const ProfitVsPublicReport = () => {
    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>();
    const [selectedInstitutionTypes, setSelectedInstitutionTypes] = useState<Option[]>([]);
    const [auspices, setAuspices] = useState<AuspiceDto[]>();
    const [selectedAuspices, setSelectedAuspices] = useState<Option[]>([]);
    const [institutions, setInstitutions] = useState<ProfitVsPublicDto[]>();
    const [total, setTotal] = useState(0);
    const [paging, setPaging] = useState<PagingDto>({
        pageNumber: 1,
        maxPageSize: null,
        pageSize: 10,
    });

    useEffect(() => {
        const loadAuspices = () => {
            institutionService
                .getAuspice()
                .then((results) => {
                    setAuspices([
                        ...(results.filter((r) => r.isActive === true).sort((a, b) => a.title.localeCompare(b.title)) ||
                            []),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        };
        const loadInstitutionTypes = () => {
            institutionService
                .getTypes()
                .then((results) => {
                    setInstitutionTypes([
                        ...(results.filter((r) => r.isActive === true).sort((a, b) => a.title.localeCompare(b.title)) ||
                            []),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading institution types');
                });
        };
        loadInstitutionTypes();
        loadAuspices();
    }, [setAuspices]);

    useEffect(() => {
        const toastId = toast.info('Searching...', {
            autoClose: false,
        });
        const getData = () => {
            reportService
                .getProfitVsPublicReport(
                    paging,
                    selectedInstitutionTypes?.map((a) => Number(a['value'])),
                    selectedAuspices?.map((a) => Number(a['value'])),
                )
                .then((response) => {
                    setInstitutions(response.records);
                    setTotal(response.total);
                    toast.update(toastId, {
                        type: 'success',
                        autoClose: 500,
                        closeOnClick: true,
                        draggable: true,
                    });
                })
                .catch(() => toast.error('Failed to generate report'));
        };

        getData();
    }, [setInstitutions, paging, selectedAuspices, selectedInstitutionTypes]);

    const exportReport = () => {
        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });
        reportService
            .exportProfitVsPublicReport(
                selectedInstitutionTypes?.map((a) => Number(a['value'])),
                selectedAuspices?.map((a) => Number(a['value'])),
            )
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `ProfitVsPublicReport.xlsx`;
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.error(`Unable to generate report`);
            });
    };
    const { handleSubmit } = useForm();

    const onSubmit = () => {
        setPaging({
            ...paging,
        });
    };

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Profit vs. Public Report</h3>
                            </div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                {auspices && auspices?.length > 0 && (
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Label for="auspice" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                            Auspice
                                        </Label>
                                        <StyledMultiSelect
                                            options={
                                                auspices?.map((a) => {
                                                    return {
                                                        label: String(a.title),
                                                        value: String(a.auspiceID),
                                                    };
                                                }) || []
                                            }
                                            disableSearch={true}
                                            hasSelectAll={true}
                                            value={selectedAuspices || []}
                                            onChange={setSelectedAuspices}
                                            labelledBy="Auspice"
                                        />
                                    </div>
                                )}
                                {institutionTypes && institutionTypes?.length > 0 && (
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Label for="institutionType" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                            Institution Type
                                        </Label>
                                        <StyledMultiSelect
                                            options={
                                                institutionTypes?.map((a) => {
                                                    return {
                                                        label: String(a.title),
                                                        value: String(a.institutionTypeId),
                                                    };
                                                }) || []
                                            }
                                            disableSearch={true}
                                            hasSelectAll={true}
                                            value={selectedInstitutionTypes || []}
                                            onChange={setSelectedInstitutionTypes}
                                            labelledBy="Institution Type"
                                        />
                                    </div>
                                )}
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Button type="submit">Search</Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-2">
                        <Button type="button" color="info" onClick={() => exportReport()}>
                            Export Report
                        </Button>
                    </div>
                    <div className="mt-3">
                        <h4>Institutions</h4>
                    </div>
                </CardBody>
                <Table>
                    <thead>
                        <tr>
                            <th>InstitutionId</th>
                            <th>Institution</th>
                            <th>Institution Type</th>
                            <th>Auspice</th>
                        </tr>
                    </thead>
                    <tbody>
                        {institutions && institutions.length > 0 ? (
                            institutions.map((institution, i) => (
                                <tr key={institution.institutionId ?? i}>
                                    <td>{institution.institutionId}</td>
                                    <td>{institution.institutionTitle}</td>
                                    <td>{institution.institutionType}</td>
                                    <td>{institution.auspiceTitle}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={4}>No Results</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                <CardBody>
                    <h4>Total: {total}</h4>
                    <Pagination size="sm" aria-label="Institutions Navigation">
                        <PaginationItem disabled={paging.pageNumber === 1}>
                            <PaginationLink
                                previous
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber - 1 })}
                            />
                        </PaginationItem>
                        <PaginationItem>
                            <PaginationLink
                                next
                                href=""
                                onClick={() => setPaging({ ...paging, pageNumber: paging.pageNumber + 1 })}
                            />
                        </PaginationItem>
                    </Pagination>
                </CardBody>
            </Card>
        </>
    );
};

export default ProfitVsPublicReport;
